import { SupportedChainId } from 'constants/chains'
import axios from 'axios'
import { ethers } from 'ethers'

const API = 'https://api.gopluslabs.io/api/v1'

const SUPPORTED_CHAINS = [SupportedChainId.MAINNET, SupportedChainId.BINANCE, SupportedChainId.ARBITRUM_ONE]

interface Holder {
  address: string
  is_locked: boolean
  tag: string
  is_contract: boolean
  balance: string // float
  percent: string // float
}
interface Dex {
  name: string
  liquidity: string // float
  pair: string
}
interface TokenSecurityDataRaw {
  // Contract Security (https://docs.gopluslabs.io/reference/token-info-response-detail/contract-security)
  is_open_source: string
  is_proxy?: string
  is_mintable?: string
  owner_address: string
  can_take_back_ownership?: string
  owner_change_balance?: string

  // Trading Security (https://docs.gopluslabs.io/reference/token-info-response-detail/trading-security)
  buy_tax?: string
  sell_tax?: string
  cannot_sell_all?: string
  slippage_modifiable?: string
  is_honeypot?: string
  transfer_pausable?: string
  is_blacklisted?: string // holder list in contract
  is_whitelisted?: string // holder list in contract
  is_in_dex: string
  dex: Dex[]
  anti_whale?: string

  token_name: string
  token_symbol: string
  holder_count: string // number
  total_supply: string
  holders: Holder[] // top 10
  owner_balance?: string // number
  owner_percent?: string // float
  creator_address: string
  creator_balance?: string // number
  creator_percent?: string // float
  lp_holder_count?: string // number
  lp_total_supply?: string // float
  lp_holders?: Holder[]
  is_true_token?: string // boolean
  is_airdrop_scam?: string // boolean
  trust_list?: string // boolean
}
export interface TokenSecurityData {
  // Contract Security
  is_open_source: boolean
  is_proxy?: boolean
  is_mintable?: boolean
  owner_address?: string
  can_take_back_ownership?: boolean
  owner_change_balance?: boolean

  // Trading Security
  buy_tax_amount?: number;
  sell_tax_amount?: number;
  buy_tax?: boolean
  sell_tax?: boolean
  cannot_sell_all?: boolean
  slippage_modifiable?: boolean
  is_honeypot?: boolean
  transfer_pausable?: boolean
  is_blacklisted?: boolean // holder list in contract
  is_whitelisted?: boolean // holder list in contract
  is_in_dex: boolean
  dex: Dex[]
  anti_whale?: boolean

  // Info Security
  token_name: string
  token_symbol: string
  holder_count: string // number
  total_supply: string
  holders: Holder[] // top 10
  owner_balance?: string // number
  owner_percent?: string // float
  creator_address: string
  creator_balance?: string // number
  creator_percent?: string // float
  lp_holder_count?: string // number
  lp_total_supply?: string // float
  lp_holders?: Holder[]
  is_true_token?: boolean
  is_airdrop_scam?: boolean
  trust_list?: boolean
}
export enum TokenSecurityState {
  SAFE = 'safe',
  UNSAFE = 'unsafe',
  UNVALIDATED = 'unvalidated',
}
export interface TokenSecurity {
  state: TokenSecurityState
  data?: TokenSecurityData
  goplusUrl?: string
}

const map: Record<number, Record<string, Promise<TokenSecurity>>> = {}

const stringToBoolean = (input: string): boolean => {
  return input === '1'
}
const stringToBooleanOptional = (input?: string): boolean | undefined => {
  if (input === undefined) {
    return undefined
  }
  return stringToBoolean(input)
}

const parseTokenSecurityDataRaw = (data: TokenSecurityDataRaw): TokenSecurityData => {
  return {
    ...data,

    // Contract Security
    is_open_source: stringToBoolean(data.is_open_source),
    is_proxy: stringToBooleanOptional(data.is_proxy),
    is_mintable: stringToBooleanOptional(data.is_mintable),
    owner_address: data.owner_address !== '' ? data.owner_address : undefined,
    can_take_back_ownership: stringToBooleanOptional(data.can_take_back_ownership),
    owner_change_balance: stringToBooleanOptional(data.owner_change_balance),

    // Trading Security
    buy_tax_amount: data.buy_tax ? parseFloat(data.buy_tax) * 100 : undefined,
    sell_tax_amount: data.sell_tax ? parseFloat(data.sell_tax) * 100 : undefined,
    buy_tax: stringToBooleanOptional(data.buy_tax),
    sell_tax: stringToBooleanOptional(data.sell_tax),
    cannot_sell_all: stringToBooleanOptional(data.cannot_sell_all),
    slippage_modifiable: stringToBooleanOptional(data.slippage_modifiable),
    is_honeypot: stringToBooleanOptional(data.is_honeypot),
    transfer_pausable: stringToBooleanOptional(data.transfer_pausable),
    is_blacklisted: stringToBooleanOptional(data.is_blacklisted),
    is_whitelisted: stringToBooleanOptional(data.is_whitelisted),
    is_in_dex: stringToBoolean(data.is_open_source),
    anti_whale: stringToBooleanOptional(data.anti_whale),

    // Info Security
    is_true_token: stringToBooleanOptional(data.is_true_token),
    is_airdrop_scam: stringToBooleanOptional(data.is_airdrop_scam),
    trust_list: stringToBooleanOptional(data.trust_list),
  }
}

export const loadTokenSecurity = async (
  chainId: SupportedChainId,
  tokenAddress: string
): Promise<TokenSecurityData | undefined> => {
  if (!SUPPORTED_CHAINS.includes(chainId)) {
    console.log('Unsupported chainId:', chainId)
    return undefined
  }

  interface TokenSecurityResponse {
    code: number // 1
    message: 'OK'
    result: Record<string, TokenSecurityDataRaw>
  }

  try {
    const result = await axios.get<TokenSecurityResponse>(
      `${API}/token_security/${chainId}?contract_addresses=${tokenAddress}`
    )

    console.log('API response:', result.data)
    console.log('API response result:', result.data.result) // Log the result object

    const tokenAddressLowercase = tokenAddress.toLowerCase()
    const tokenSecurityDataRaw = result.data.result[tokenAddressLowercase]

    if (!tokenSecurityDataRaw) {
      console.log('Token data not found for address:', tokenAddress)
      return undefined
    }

    return parseTokenSecurityDataRaw(tokenSecurityDataRaw)
  } catch (e) {
    console.log('Error in loadTokenSecurity:', e)
    return undefined
  }
}

export const isSafeToken = (data: TokenSecurityData) => {
  if (!data.is_open_source) {
    return false // unable to verify - dangerous
  }

  if (data.is_airdrop_scam === true || data.is_true_token === false) {
    return false // clearly scam
  }
  if (data.trust_list === true || data.is_true_token === true) {
    return true // trusted
  }

  if ((data.buy_tax_amount && data.buy_tax_amount > 15) || (data.sell_tax_amount && data.sell_tax_amount > 15)) {
    return false // can't be used and potential scam
  }
  if (data.owner_change_balance === true) {
    return false // owner can steal funds
  }
  if (data.is_honeypot === true) {
    return false
  }

  return true
}

const generateTokenSecurity = async (chainId: SupportedChainId, tokenAddress: string): Promise<TokenSecurity> => {
  // handle native tokens
  if (tokenAddress === ethers.constants.AddressZero) {
    return {
      state: TokenSecurityState.SAFE,
    }
  }

  const data = await loadTokenSecurity(chainId, tokenAddress)

  const secure = data ? isSafeToken(data) : false

  const tokenSecurity = {
    state: data ? (secure ? TokenSecurityState.SAFE : TokenSecurityState.UNSAFE) : TokenSecurityState.UNVALIDATED,
    data: data,
    goplusUrl: data ? `https://gopluslabs.io/token-security/${chainId}/${tokenAddress}` : undefined,
  }
  return tokenSecurity
}
